import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { apiSlice } from 'store/slices/apiSlice';
import { isSuperadmin } from 'utils/rbacUtils';

const DATAHUB_ROLES = [
    { value: 'user', label: 'User' },
    { value: 'tenant_admin', label: 'Tenant Admin' }
];

const UserManagement = () => {
    const account = useSelector((state) => state.account);
    const isSuperadminRole = isSuperadmin(account?.user);
    const dispatch = useDispatch();
    const { data: usersResponse, isLoading, error } = apiSlice.useGetUsersQuery({}, { skip: !isSuperadminRole });
    const [updateUser] = apiSlice.useUpdateUserByIdMutation();
    const [selectedUser, setSelectedUser] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const users = usersResponse?.data ?? [];
    const count = usersResponse?.count ?? 0;

    const handleUpdateUser = async (userId, updates) => {
        setSaveError(null);
        try {
            await updateUser({ userId, ...updates }).unwrap();
            setSelectedUser(null);
            setOpenDialog(false);
        } catch (err) {
            setSaveError(err?.data?.detail || err?.message || 'Cập nhật thất bại');
        }
    };

    if (!isSuperadminRole) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">Chỉ Superadmin mới truy cập trang quản lý user.</Alert>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Typography>Đang tải...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Quản lý tài khoản (Superadmin)
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error?.data?.detail || 'Không tải được danh sách user'}
                </Alert>
            )}

            <Card>
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Họ tên</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Tenant ID</TableCell>
                                <TableCell>Vai trò DataHub</TableCell>
                                <TableCell>Trạng thái</TableCell>
                                <TableCell>Thao tác</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>{user.full_name || '—'}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.tenant_id ?? '—'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={DATAHUB_ROLES.find((r) => r.value === user.datahub_role)?.label || user.datahub_role}
                                            color={user.datahub_role === 'tenant_admin' ? 'primary' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={user.is_active ? 'Hoạt động' : 'Tắt'} size="small" color={user.is_active ? 'success' : 'default'} />
                                    </TableCell>
                                    <TableCell>
                                        <Button size="small" onClick={() => { setSelectedUser(user); setOpenDialog(true); }}>
                                            Sửa
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Tổng: {count} user
                    </Typography>
                </CardContent>
            </Card>

            {openDialog && selectedUser && (
                <UserEditorDialog
                    user={selectedUser}
                    onSave={(updates) => handleUpdateUser(selectedUser.id, updates)}
                    onCancel={() => { setSelectedUser(null); setOpenDialog(false); setSaveError(null); }}
                    saveError={saveError}
                />
            )}
        </Box>
    );
};

const UserEditorDialog = ({ user, onSave, onCancel, saveError }) => {
    const [formData, setFormData] = useState({
        full_name: user?.full_name ?? '',
        email: user?.email ?? '',
        tenant_id: user?.tenant_id ?? '',
        datahub_role: user?.datahub_role ?? 'user',
        is_active: user?.is_active ?? true
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            full_name: formData.full_name || null,
            email: formData.email || null,
            tenant_id: formData.tenant_id === '' ? null : Number(formData.tenant_id),
            datahub_role: formData.datahub_role,
            is_active: formData.is_active
        };
        onSave(payload);
    };

    return (
        <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>Sửa user</DialogTitle>
            <DialogContent>
                {saveError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {saveError}
                    </Alert>
                )}
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                    <TextField
                        fullWidth
                        label="Họ tên"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Tenant ID"
                        type="number"
                        value={formData.tenant_id}
                        onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                        margin="normal"
                        inputProps={{ min: 0, step: 1 }}
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Vai trò DataHub</InputLabel>
                        <Select
                            value={formData.datahub_role}
                            onChange={(e) => setFormData({ ...formData, datahub_role: e.target.value })}
                            label="Vai trò DataHub"
                        >
                            {DATAHUB_ROLES.map((r) => (
                                <MenuItem key={r.value} value={r.value}>
                                    {r.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Trạng thái</InputLabel>
                        <Select
                            value={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                            label="Trạng thái"
                        >
                            <MenuItem value>Hoạt động</MenuItem>
                            <MenuItem value={false}>Tắt</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Hủy</Button>
                <Button onClick={handleSubmit} variant="contained">
                    Cập nhật
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserManagement;
